import type { ReactElement } from 'react';
import { formatNumber } from '@xdocs/i18n';
import { EmptyState, useI18n } from '@xdocs/ui';
import { BarChart, KpiTile, RoleGate, useAdminAnalyticsOverview, useAdminT } from '../../../features/admin/index.js';
import styles from './AnalyticsPage.module.css';

/** `/admin/analytics` (F71 client, design/app/43-admin-analytics.html):
 * DAU/WAU/MAU + generation-success KPI row, generation-jobs-by-worker bar
 * chart, conversion funnel, top public assets — all over the SAME cached
 * `admin.analytics.overview` read-model `/admin/overview` uses (no second
 * fetch of the same data under a different shape). */
export function AnalyticsPage(): ReactElement {
  return (
    <RoleGate minimum="admin">
      <AnalyticsContent />
    </RoleGate>
  );
}

function AnalyticsContent(): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useAdminT();
  const { overview, isLoading, isError, error, refetch } = useAdminAnalyticsOverview();

  if (isError) {
    return <EmptyState kind="error" title={t('admin:analytics.error.title')} description={error?.message} action={{ label: tCommon('common:action.retry'), onClick: refetch }} />;
  }

  if (!isLoading && overview && overview.dau === 0 && overview.mau === 0) {
    return <EmptyState kind="first-use" title={t('admin:analytics.empty.title')} description={t('admin:analytics.empty.description')} />;
  }

  const totalSucceeded = overview?.generationByWorker.reduce((sum, w) => sum + w.succeeded, 0) ?? 0;
  const totalAttempted = overview?.generationByWorker.reduce((sum, w) => sum + w.succeeded + w.failed, 0) ?? 0;
  const genSuccessPercent = totalAttempted > 0 ? (totalSucceeded / totalAttempted) * 100 : null;

  const workerChartData = (overview?.generationByWorker ?? []).map((w) => ({ label: w.workerType, value: w.succeeded }));

  return (
    <div className={styles['page']}>
      <h1 className={styles['title']}>{t('admin:analytics.title')}</h1>

      <div className={styles['grid']}>
        <KpiTile label={t('admin:analytics.kpi.dau')} value={overview ? formatNumber(locale, overview.dau) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:analytics.kpi.wau')} value={overview ? formatNumber(locale, overview.wau) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:analytics.kpi.mau')} value={overview ? formatNumber(locale, overview.mau) : '—'} loading={isLoading} />
        <KpiTile
          label={t('admin:analytics.kpi.genSuccess')}
          value={genSuccessPercent !== null ? `${genSuccessPercent.toFixed(1)}%` : '—'}
          loading={isLoading}
        />
      </div>

      <div className={styles['chartPanel']}>
        <div className={styles['chartTitle']}>{t('admin:analytics.chart.byWorker.title')}</div>
        <BarChart
          data={workerChartData}
          ariaLabel={t('admin:analytics.chart.byWorker.ariaLabel')}
          valueFormatter={(v) => formatNumber(locale, v)}
          categoryColumnLabel={t('admin:chart.column.category')}
          valueColumnLabel={t('admin:chart.column.value')}
        />
      </div>

      <div className={styles['sections']}>
        <div className={styles['panel']}>
          <div className={styles['panelTitle']}>{t('admin:analytics.conversion.title')}</div>
          <div className={styles['statRow']}>
            <span>{t('admin:analytics.conversion.freeToPro')}</span>
            <span className={styles['statValue']}>{overview ? `${overview.freeToProConversionPercent.toFixed(1)}%` : '—'}</span>
          </div>
          <div className={styles['statRow']}>
            <span>{t('admin:analytics.conversion.signupToFirstDoc')}</span>
            <span className={styles['statValue']}>{overview ? `${overview.signupToFirstDocPercent.toFixed(1)}%` : '—'}</span>
          </div>
        </div>

        <div className={styles['panel']}>
          <div className={styles['panelTitle']}>{t('admin:analytics.topAssets.title')}</div>
          {overview && overview.topPublicAssets.length === 0 && <p className={styles['empty']}>{t('admin:analytics.topAssets.empty')}</p>}
          {overview && overview.topPublicAssets.length > 0 && (
            <ul className={styles['assetList']}>
              {overview.topPublicAssets.map((asset) => (
                <li key={asset.shareId} className={styles['assetRow']}>
                  <span>{asset.documentTitle}</span>
                  <span className={styles['assetViews']}>{t('admin:analytics.topAssets.views', { count: asset.viewCount })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
